import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import middleware from "../../../Api/Middleware";
import ToastError from "../../../Toasts/ToastError";
import ToastSuccess from "../../../Toasts/ToastSuccess";
import PageTemplate from "../../../Templates/PageTemplate";
import TextEditor from "../../../Main/TextEditor";
import { BackButton } from "../../../HouseStyle/Components/Buttons";
import noimage from "./noimage.jpg";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BorderedDropDown } from "../../../HouseStyle/Components/DropDowns";
import SalesChannelDescriptionEditor from "../../../HouseStyle/Components/SalesChannelDescriptionEditor";

const EditCategorySetting = () => {
  const navigate = useNavigate();
  const { category_id } = useParams();
  const [category, setCategory] = useState({ title: "", description: "", slug: "" });
  const [seo, setSeo] = useState({
    title: "",
    image: "",
    keywords: "",
    description: "",
  });
  const [oldCategory, setOldCategory] = useState({});
  const [oldSeo, setOldSeo] = useState({});
  const [availableCategories, setAvailableCategories] = useState([]);
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [changedImage, setChangedImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [seoAppInstalled, setSeoAppInstalled] = useState(() => {
    const appstore = JSON.parse(localStorage.getItem("appstore"));
    return appstore?.apps?.find(app => app.slug === "seo-duplicates")?.status === 3;
  });
  const imageInputRef = useRef(null);
  const saveTimeoutRef = useRef();

  // Helper function to get the correct image source
  const getImageSrc = () => {
    if (!category?.image) {
      return noimage;
    }
    
    // If it's base64 data (newly uploaded), return it directly
    if (category.image.startsWith('data:image/')) {
      return category.image;
    }
    
    // If it's a file path (from backend), construct the full URL
    return middleware.defaults.imagesBaseURL + category.image;
  };

  // Helper function to check if objects have differences
  const hasRealChanges = (obj1, obj2) => {
    if (!obj1 || !obj2) return false;

    const fieldsToCompare = ['title', 'description', 'head_category_id', 'image', 'slug', 'keywords'];
    return fieldsToCompare.some(field => {
      if (!(field in obj1) || !(field in obj2)) return false;
      
      // Handle image field specially - treat null, undefined, and empty string as equivalent
      if (field === 'image') {
        const val1 = obj1[field] || '';
        const val2 = obj2[field] || '';
        return val1 !== val2;
      }
      
      return obj1[field] !== obj2[field];
    });
  };

  // Debounced save function
  const debouncedSave = (immediate = false) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const delay = immediate ? 0 : 300; // Shorter delay for onBlur
    
    saveTimeoutRef.current = setTimeout(async () => {
      const categoryChanged = hasRealChanges(category, oldCategory);
      // const seoChanged = hasRealChanges(seo, oldSeo);

      // if (!categoryChanged && !seoChanged) {
      //   return; // No changes to save
      // }

      if (!categoryChanged) {
        return; // No changes to save
      }

      try {
        setIsSaving(true);
        
        if (!category.title || category.title.trim() === "") {
          ToastError("Naam mag niet leeg zijn of alleen uit spaties bestaan.");
          setCategory(oldCategory);
          return;
        }

        // Update category if changed
        if (categoryChanged) {
          await middleware.put(
            `/products/category?category_id=${category_id}`,
            {
              ...category,
              category_id: category_id
            }
          );
        }

        // // Handle SEO updates
        // if (seoChanged) {
        //   if (oldSeo && oldSeo.seo_id) {
        //     await middleware.put(
        //       `/products/seos?seo_id=${oldSeo.seo_id}`,
        //       {
        //         ...seo,
        //         seo_id: oldSeo.seo_id,
        //         category_id: category_id
        //       }
        //     );
        //   } else if (
        //     seo && seo.description && seo.keywords && seo.title
        //   ) {
        //     // Create new SEO entry
        //     const res = await middleware.post(
        //       `/products/seos`,
        //       {
        //         ...seo,
        //         category_id: category_id
        //       }
        //     );
            
        //     if (res.data && res.data.data && res.data.data.seo_id) {
        //       const seoWithId = { ...seo, seo_id: res.data.data.seo_id };
        //       setSeo(seoWithId);
        //       setOldSeo(JSON.parse(JSON.stringify(seoWithId)));
        //     }
        //   }
        // }

        // Update the "old" states to reflect current saved state
        setOldCategory(JSON.parse(JSON.stringify(category)));
        // if (seo) setOldSeo(JSON.parse(JSON.stringify(seo)));
        
        ToastSuccess("Categorie opgeslagen.", null, 3000);
      } catch (e) {
        console.error(e);
        ToastError("Er is een fout opgetreden tijdens het opslaan.");
      } finally {
        setIsSaving(false);
      }
    }, delay);
  };

  const fetchCategory = async () => {
    try {
      const [categoryRes, availableCategoriesRes, seoRes] = await Promise.all([
        middleware.get(`/products/category?category_id=${category_id}`),
        middleware.get(`/products/category?possibleHeadCategories=${category_id}`),
        // middleware.get(`/products/seos?category_id=${category_id}`),
      ]);

      if (categoryRes.data.data) {
        // Ensure empty image is handled properly
        const categoryData = {
          ...categoryRes.data.data,
          image: categoryRes.data.data.image || ""
        };
        setCategory(categoryData);
        setOldCategory(JSON.parse(JSON.stringify(categoryData)));
        setStatus(categoryData.status);
      } else {
        console.error("No category data received");
        ToastError("Geen categoriegegevens gevonden.");
      }

      // if (seoRes.data.data && seoRes.data.data[0]) {
      //   setSeo(seoRes.data.data[0]);
      //   setOldSeo(JSON.parse(JSON.stringify(seoRes.data.data[0])));
      // } else {
      //   // Set SEO to empty object instead of undefined
      //   setSeo({
      //     title: "",
      //     image: "",
      //     keywords: "",
      //     description: "",
      //   });
      //   setOldSeo({});
      // }

      // Set available categories, filtering out the current one
      if (availableCategoriesRes?.data?.data) {
        setAvailableCategories(
          availableCategoriesRes.data.data.filter(
            (e) => e.category_id !== Number(category_id)
          )
        );
      } else {
        setAvailableCategories([]);
      }

      setLoading(false);
    } catch (error) {
      if (error.response.status === 404 || error.response.status === 400) {
        ToastError('Deze categorie bestaat niet.');
        navigate('/algemene/instellingen/product/categorieën');
      } else {
        console.error("Error fetching category data:", error);
        ToastError("Er is een fout opgetreden bij het ophalen van de categoriegegevens.");
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      const validImageTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/webp",
        "image/gif",
      ];

      if (!validImageTypes.includes(fileType)) {
        ToastError(
          "Ongeldig foto. Alleen PNG, JPG, JPEG, WEBP, en GIF zijn toegestaan."
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const updatedCategory = { ...category, image: e.target.result };
        setCategory(updatedCategory);
        setChangedImage(true);
        
        // Save immediately with the updated category
        saveImageChange(updatedCategory);
        
        if (imageInputRef.current) {
          imageInputRef.current.value = null;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    const updatedCategory = { ...category, image: null };
    setCategory(updatedCategory);
    setChangedImage(true);
    
    // Save immediately with the updated category
    saveImageChange(updatedCategory);
  };

  // Separate function to handle image saves immediately
  const saveImageChange = async (updatedCategory) => {
    try {
      setIsSaving(true);
      
      await middleware.put(
        `/products/category?category_id=${category_id}`,
        {
          ...updatedCategory,
          category_id: category_id
        }
      );

      // Update the "old" category to reflect the saved state
      setOldCategory(JSON.parse(JSON.stringify(updatedCategory)));
      
      ToastSuccess("Afbeelding opgeslagen.");
    } catch (e) {
      console.error(e);
      ToastError("Er is een fout opgetreden tijdens het opslaan van de afbeelding.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm("Weet je zeker dat je deze categorie wil verwijderen?")
    ) {
      try {
        setLoading(true);
        
        // Delete SEO first if it exists
        // if (seo && seo.seo_id) {
        //   await middleware.delete(`/products/seos?seo_id=${seo.seo_id}`);
        // }
        
        // Then delete the category
        await middleware.delete(
          `/products/category?category_id=${category.category_id}`
        );

        ToastSuccess("Categorie succesvol verwijderd.");
        navigate("/algemene/instellingen/product/categorieën");
      } catch (e) {
        console.error(e);
        ToastError("Er ging iets mis bij het verwijderen van de categorie.");
        setLoading(false);
      }
    }
  };

  // Handle status changes immediately
  useEffect(() => {
    if (loading) return;

    // Only update if status actually changed
    if (category.status !== status) {
      const updateStatus = async () => {
        try {
          setIsSaving(true);
          const updatedCategory = { ...category, status };
          await middleware.put(
            `/products/category?category_id=${category_id}`,
            updatedCategory
          );
          setCategory(updatedCategory);
          setOldCategory(JSON.parse(JSON.stringify(updatedCategory)));
          ToastSuccess(status ? "Categorie gepubliceerd." : "Categorie gearchiveerd.");
        } catch (error) {
          console.error(error);
          ToastError("Er is een fout opgetreden bij het bijwerken van de status.");
          setStatus(category.status);
        } finally {
          setIsSaving(false);
        }
      };

      updateStatus();
    }
  }, [status, loading, category.status]);

  useEffect(() => {
    debouncedSave();
  }, [category.head_category_id, category.description]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <PageTemplate
      pageIsLoading={loading}
      navbarTitle={"Categorie Bewerken"}
    >
      <div className="d-flex w-100 flex-column">
        <div className="d-flex mb-4 mt-4 flex-row justify-content-between">
          <div className="d-flex">
            <div className="d-flex flex-row me-5 align-items-center">
              <BackButton text={"Terug"} disabled={false} onClickFunction={() => navigate('/algemene/instellingen/product/categorieën')}/>
            </div>
            <div className="d-flex flex-column align-items-center">
              {status ? (
                <p className="mb-1 p-0">Gepubliceerd</p>
              ) : (
                <p className="mb-1 p-0">Gearchiveerd</p>
              )}
              <label className="switchActiveProduct">
                <input
                  type="checkbox"
                  checked={status}
                  onChange={(e) => setStatus(e.target.checked)}
                  disabled={isSaving}
                />
                <span className="sliderActiveProduct round"></span>
              </label>
            </div>
          </div>
          <div>
            <button 
              className="darkBlueStandardButton" 
              onClick={handleDelete}
              disabled={isSaving}
            >
              Verwijderen
            </button>
          </div>
        </div>
        <div
          className="whiteBox w-100 px-5 py-4 fitcontentHeight"
          style={{ minheight: "80vh" }}
        >
          <div className="d-flex flex-row justify-content-between pb-3 mb-3">
            <h3 className="mb-0 fw-bold">Categorie aanpassen</h3>
            {isSaving && (
              <small className="text-muted">Opslaan...</small>
            )}
          </div>

          <div>
            <div className="d-flex flex-row w-100 mb-3">
              <div className="d-flex flex-column w-50 pe-3">
                <label className="mb-2 fw-semibold">Naam</label>
                <input
                  className="standardInput"
                  onChange={(e) =>
                    setCategory({ ...category, title: e.target.value })
                  }
                  onBlur={() => debouncedSave()}
                  value={category?.title || ""}
                />
              </div>
              <div className="d-flex flex-column w-50">
                <label className="mb-2 fw-semibold">Slug</label>
                <input
                  className="standardInput"
                  value={category?.slug || ""}
                  onChange={(e) => setCategory({ ...category, slug: e.target.value })}
                  onBlur={() => debouncedSave()}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="mb-2 fw-semibold">Hoofdcategorie</label>
              <BorderedDropDown
                placeholder={'Selecteer een hoofdcategorie'}
                searchable={true}
                selectedValue={category?.head_category_id}
                setSelectedValue={(value) => {
                  setCategory({ ...category, head_category_id: value });
                }}
                options={[
                  { title: 'Geen', value: '' },
                  ...availableCategories.map((e, index) => ({
                    title: e.title,
                    value: e.category_id
                  }))
                ]}
              />
              {/* <select
                value={category?.head_category_id || ""}
                onChange={(e) => {
                  setCategory({ ...category, head_category_id: e.target.value });
                  // Save immediately for select changes
                  setTimeout(() => debouncedSave(true), 100);
                }}
                className="standardInput standardDropdownArrow w-100"
              >
                <option hidden>Selecteer een hoofdcategorie</option>
                <option value={""}>Geen</option>
                {availableCategories &&
                  availableCategories.map((e, index) => (
                    <option key={index} value={e.category_id}>
                      {e.title}
                    </option>
                  ))}
              </select> */}
            </div>
            <div className="mb-3">
              {seoAppInstalled ? (
                <SalesChannelDescriptionEditor
                  entityType="category"
                  entityId={category_id}
                  defaultDescription={category?.description || ""}
                  onDefaultDescriptionChange={(value) => {
                    setCategory({ ...category, description: value });
                    debouncedSave();
                  }}
                  isEditMode={true}
                  useTextarea={false}
                  label="Omschrijving"
                />
              ) : (
                <>
                  <label className="mb-2 fw-semibold">Omschrijving</label>
                  <TextEditor
                    content={category?.description || ""}
                    setContent={(value) => {
                      setCategory({ ...category, description: value });
                      debouncedSave();
                    }}
                  />
                </>
              )}
            </div>
            <div className="mb-5">
              <label className="mb-2 fw-semibold">Afbeelding</label>

              <div className="d-flex flex-row gap-2">
                <div className="editCategoryImage">
                  <img src={getImageSrc()} alt="Categorie" />
                </div>
                <div className="uploadImageBox">
                  <label 
                    htmlFor="imageUpload" 
                    className="btn btn-techdog"
                  >
                    Nieuwe foto
                  </label>
                  <input
                    type="file"
                    className="imageUpload"
                    name="imageUpload"
                    id="imageUpload"
                    ref={imageInputRef}
                    accept=".png,.jpg,.jpeg,.webp,.gif"
                    onChange={(event) => handleImageUpload(event)}
                  />
                </div>
                <div className="uploadImageBox">
                  <div
                    type="button"
                    className={`btn btn-danger ${!category?.image || category.image === "" ? "disabled" : ""}`}
                    disabled={!category?.image || category.image === ""}
                    onClick={handleImageDelete}
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </div>
                  <div></div>
                </div>
              </div>
            </div>
          </div>
           {/* <div className='mt-4'>
            <h3 className=' fw-semibold mb-3'>SEO</h3>
            <div className='d-flex flex-column mb-3'>
              <label className='mb-2 fw-semibold'>SEO titel</label>
              <input className='standardInput' value={seo?.title || ''} onChange={(e) => setSeo({ ...seo, title: e.target.value })} />
            </div>
            <div className='d-flex flex-column mb-3'>
              <label className='mb-0 fw-semibold'>SEO zoekwoorden</label>
              <small style={{ color: "#c9c9c9" }} className='mb-2'>Vul een zoekwoord in en druk op enter om het zoekwoord toe te voegen.</small>
              <textarea className='standardInput p-2' style={{ height: "125px" }} value={seo?.keywords || ''} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} />
            </div>
            <div className='d-flex flex-column mb-4'>
              <label className='mb-2 fw-semibold'>SEO omschrijving</label>
              <textarea className='standardInput p-2' style={{ height: "125px" }} value={seo?.description || ''} onChange={(e) => setSeo({ ...seo, description: e.target.value })} />
            </div>
          </div> */}
        </div>
      </div>
    </PageTemplate>
  );
};

export default EditCategorySetting;